import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, Medal, Flame, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageExplain } from "@/components/PageExplain";
import { UserLink, UserAvatar } from "@/components/UserBits";
import { LevelChip } from "@/components/LevelUp";
import { useVipIds } from "@/lib/vip";
import { SupporterBadge } from "@/components/RoleBadge";
import { Glitter } from "@/components/SupporterGlitter";

const PODIUM = ["from-amber-300 to-amber-500", "from-slate-200 to-slate-400", "from-orange-300 to-orange-500"];

const Avatar = ({ e, size = "w-11 h-11" }) => <UserAvatar userId={e.user_id} name={e.name} picture={e.picture} size={size} />;
const BlockedNote = ({ id }) => <span className="inline-block mt-1 -rotate-6 px-2 py-0.5 border-2 border-red-600 rounded-md text-[10px] font-black tracking-wider text-red-600 bg-white/80" data-testid={`leaderboard-blocked-${id}`}>WEGEN REGELVERSTOẞ GESPERRT</span>;
const Stamp = ({ id }) => (
  <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10" aria-label="Wegen Regelverstoß gesperrt" data-testid={`leaderboard-blocked-${id}`}>
    <span className="stamp-in px-3 py-1 sm:px-4 sm:py-1.5 rounded-lg border-[3px] border-double border-red-600 text-red-600 bg-white/70 backdrop-blur-[1px] font-black text-xs sm:text-sm tracking-[0.15em] whitespace-nowrap shadow-[0_0_0_2px_rgba(220,38,38,0.25)]"
      style={{ maskImage: "radial-gradient(circle at 30% 40%, #000 60%, rgba(0,0,0,.75) 61%, #000 75%)" }}>
      WEGEN REGELVERSTOẞ GESPERRT
    </span>
  </div>
);

const RankIcon = ({ rank }) => {
  if (rank === 1) return <Crown className="w-5 h-5 text-amber-500" />;
  if (rank <= 3) return <Medal className={`w-5 h-5 ${rank === 2 ? "text-slate-400" : "text-orange-500"}`} />;
  return <span className="w-5 text-center font-display font-extrabold text-slate-400">{rank}</span>;
};

const LastWinners = ({ w }) => (
  <div className="bg-white rounded-3xl p-5 border border-amber-200" data-testid="last-winners">
    <p className="text-xs uppercase tracking-widest font-semibold text-amber-600 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4" /> Gewinner {w.label}</p>
    <div className="grid grid-cols-3 gap-3">
      {w.winners.map((x) => (
        <div key={x.place} className="flex flex-col items-center text-center gap-1.5" data-testid={`last-winner-${x.place}`}>
          <div className={`rounded-full p-0.5 bg-gradient-to-br ${PODIUM[x.place - 1]}`}><Avatar e={x} size="w-14 h-14" /></div>
          <p className="text-sm font-semibold truncate max-w-full"><UserLink id={x.user_id}>{x.name}</UserLink></p>
          {x.blocked && <BlockedNote id={x.user_id} />}
          <p className="text-xs font-bold text-amber-600">Platz {x.place} · +{x.prize}</p>
        </div>
      ))}
    </div>
  </div>
);

const PERIODS = [["month", "Diesen Monat"], ["all", "Gesamt"], ["friends", "Freunde"], ["levels", "Level-Aufsteiger"]];

const leaderboardSteps = (data) => [
  "Hier siehst du die 10 fleißigsten Fans – gezählt werden alle gesammelten Punkte (Aktivitäten, Login, Einladungen, Spiele, Boni).",
  "Wichtig: Die Zahl oben in der Kopfzeile ist dein Kontostand – also was du gerade zum Einlösen hast. Hier zählen dagegen die gesammelten Punkte, ausgegebene Punkte werden nicht abgezogen.",
  "Eingelöste Geschenke ziehen nichts ab – du verlierst also keinen Platz, wenn du dir etwas gönnst.",
  "„Diesen Monat“ startet jeden 1. neu – so hat jeder die Chance auf Platz 1!",
  "Unter „Freunde“ vergleichst du dich nur mit deinen Freunden (alle Punkte gesamt).",
  `Am 1. jedes Monats bekommen die Top 3 des Vormonats automatisch Bonuspunkte (+${data?.prizes?.[0] ?? 100} / +${data?.prizes?.[1] ?? 50} / +${data?.prizes?.[2] ?? 25}) und eine Glückwunsch-Push-Nachricht.`,
];

const levelSteps = [
  "Hier siehst du, wer in diesem Monat die meisten Level aufgestiegen ist.",
  "Jeder Level-Aufstieg zählt – auch wenn du nach einer Geschenk-Einlösung ein Level erneut erreichst.",
  "Bei Gleichstand liegt vorne, wer seine Level zuerst geschafft hat. Am 1. jedes Monats startet die Liste neu.",
];

export default function Leaderboard() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const vipIds = useVipIds();
  const isLv = period === "levels";
  const { user } = useAuth();
  const scoreLabel = period === "month" ? "gesammelt diesen Monat" : "gesammelt gesamt";
  useEffect(() => { setData(null); api.get(`/leaderboard?period=${period}`).then(({ data }) => setData(data)); }, [period]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-wider uppercase font-semibold text-rose-600">Rangliste</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><Trophy className="w-8 h-8 text-amber-500" /> {period === "levels" ? "Top 10 Level-Aufsteiger" : "Top 10 Punktesammler"}</h1>
        <div className="flex gap-4 flex-wrap mt-2"><Link to="/community" data-testid="leaderboard-community-link" className="text-sm font-semibold text-rose-600 hover:underline">Alle Mitglieder ansehen →</Link>
        <Link to="/ruhmeshalle" data-testid="leaderboard-hof-link" className="text-sm font-semibold text-amber-600 hover:underline">🏆 Zur Ruhmeshalle →</Link></div>
      </div>
      <PageExplain testId="leaderboard-explain" steps={period === "levels" ? levelSteps : leaderboardSteps(data)} key={period === "levels" ? "lv" : "pts"} />
      <div className="flex gap-2 flex-wrap">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`leaderboard-period-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${period === k ? "bg-rose-500 text-white" : "bg-white border border-pink-100 text-slate-600 hover:bg-rose-50"}`}>{l}</button>
        ))}
      </div>
      {!data ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div> : (
        <>
          <div className="rounded-3xl pink-gradient text-white p-5 flex items-center justify-between pink-glow" data-testid="leaderboard-my-rank">
            <div><p className="text-xs uppercase tracking-widest text-pink-100">Dein Platz</p><p className="font-display font-extrabold text-3xl">{data.my_rank ? `#${data.my_rank}` : "–"} <span className="text-base font-semibold text-pink-100">von {data.total}</span></p></div>
            <div className="text-right"><p className="text-xs uppercase tracking-widest text-pink-100" data-testid="leaderboard-score-label">{isLv ? "Level-Aufstiege" : scoreLabel}</p><p className="font-display font-extrabold text-3xl" data-testid="leaderboard-my-score">{data.my_score}</p></div>
          </div>
          {!isLv && user && (
            <p className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800" data-testid="leaderboard-balance-hint">
              Dein <b>Kontostand</b> (oben in der Kopfzeile): <b data-testid="leaderboard-balance">{user.points}</b> Punkte – das ist, was du gerade einlösen kannst. Die Rangliste zählt dagegen alle <b>gesammelten</b> Punkte{period === "month" ? " seit dem 1. des Monats" : ""}; Ausgaben ziehen hier nichts ab.
            </p>
          )}
          {!isLv && data.last_winners?.winners?.length > 0 && <LastWinners w={data.last_winners} />}
          <ol className="bg-white rounded-3xl border border-pink-100 divide-y divide-pink-50 overflow-hidden" data-testid="leaderboard-list">
            {data.entries.length === 0 && <li className="p-8 text-center text-sm text-slate-400" data-testid="leaderboard-empty">{period === "friends" ? "Noch keine Punkte unter deinen Freunden – füge in der Community Freunde hinzu!" : isLv ? "Diesen Monat ist noch niemand aufgestiegen – sammle Punkte und sei die/der Erste!" : "Noch keine Punkte gesammelt – sei die/der Erste!"}</li>}
            {data.entries.map((e, i) => (
              <li key={e.user_id} data-testid={`leaderboard-entry-${e.rank}`} className={`fade-up relative flex items-center gap-3 sm:gap-4 p-4 ${vipIds.has(e.user_id) ? "sp-surface sp-glow sp-glow-in" : e.is_me ? "bg-rose-50" : ""}`} data-vip={vipIds.has(e.user_id) || undefined} style={{ animationDelay: `${i * 40}ms` }}>
                {vipIds.has(e.user_id) && <Glitter size="sm" />}
                {e.blocked && <Stamp id={e.user_id} />}
                <div className={`contents ${e.blocked ? "[&>*]:opacity-40 [&>*]:grayscale" : ""}`}>
                <div className="w-6 flex justify-center"><RankIcon rank={e.rank} /></div>
                <div className={e.rank <= 3 ? `rounded-full p-0.5 bg-gradient-to-br ${PODIUM[e.rank - 1]}` : ""}><Avatar e={e} /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate"><UserLink id={e.user_id} testId={`leaderboard-user-link-${e.rank}`}>{e.name}</UserLink> {e.is_me && <span className="text-xs text-rose-500">(Du)</span>}</p>
                  {vipIds.has(e.user_id) && <span className="block mt-0.5"><SupporterBadge chip testId={`leaderboard-supporter-${e.rank}`} /></span>}
                  {isLv && <div className="mt-0.5"><LevelChip points={e.points ?? 0} testId={`leaderboard-level-${e.rank}`} /></div>}
                  {!isLv && e.streak > 0 && <p className="text-xs text-slate-400 flex items-center gap-1"><Flame className="w-3 h-3 text-orange-500" /> {e.streak} Tage Serie</p>}
                </div>
                <span className="font-display font-extrabold text-lg text-amber-600 text-right leading-tight">{isLv ? <>+{e.score}<span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Level</span></> : <>{e.score}<span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">gesammelt</span></>}</span>
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
